import React, { useMemo } from "react";
import { Euler } from "three";
import { useConfigurationOptionsContext } from "../../../../../app/providers/DoorOptionsProvider";
import {
  DoorGlassMaterial,
  getFramePositions,
} from "../../../../../shared/utils/door";
import {
  isDeBlaakCode,
  isDeMeentCode,
} from "entities/configurator/model/layoutMeta";

interface KomHandleProps {
  doorNodes: any;
  materials: DoorGlassMaterial;
  index: number;
  shiftZValue?: number;
  showKomGreepBack?: boolean;
}

const KomHandle: React.FC<KomHandleProps> = React.memo(
  ({
    doorNodes,
    materials,
    index,
    shiftZValue,
    showKomGreepBack = true,
  }: KomHandleProps) => {
    const rotate = useMemo(() => new Euler(0, Math.PI), []);

    const {
      getConfigurationHeight,
      getConfigurationWidth,
      getConfigurationColor,
      layout,
    } = useConfigurationOptionsContext();

    const positions = useMemo(
      () =>
        getFramePositions(
          getConfigurationWidth(index),
          getConfigurationHeight(index),
        ),
      [getConfigurationWidth, getConfigurationHeight, index],
    );

    const doorHeight = getConfigurationHeight(index);

    // Calculate the handle height adjustments based on layout and door height
    const handleHeightAdjustments = useMemo(() => {
      if (isDeBlaakCode(layout?.code) && doorHeight >= 2800) {
        return 0.1;
      } else if (isDeMeentCode(layout?.code) && doorHeight <= 2224) {
        // Lerp from 0.1 to 0.2 as doorHeight goes from 1900 to 2224
        const minHeight = 1900;
        const maxHeight = 2224;
        if (doorHeight <= minHeight) return 0.09;
        if (doorHeight >= maxHeight) return 0.2;
        const t = (doorHeight - minHeight) / (maxHeight - minHeight);
        return 0.1 + t * (0.2 - 0.1);
      }
      return 0;
    }, [layout?.code, doorHeight]);

    return (
      <group position-z={shiftZValue ?? 0}>
        <mesh
          geometry={doorNodes["handle_komgreep_front"].geometry}
          material={materials[getConfigurationColor()]}
          position={[
            positions.left.x + 0.015,
            -0.175 + handleHeightAdjustments,
            0,
          ]}
        ></mesh>

        {showKomGreepBack && (
          <group rotation={rotate}>
            <mesh
              geometry={doorNodes["handle_komgreep_back"].geometry}
              material={materials[getConfigurationColor()]}
              position={[
                positions.right.x + 0.015,
                -0.175 + handleHeightAdjustments,
                0,
              ]}
            ></mesh>
          </group>
        )}
      </group>
    );
  },
);

export default KomHandle;
